import { moduleApi } from "@amtana/module";
import type { EraseStep } from "@amtana/module";

import type {
  BulkEditInput,
  BulkEditResult,
  ConnectApiKeyInput,
  ConnectOAuthInput,
  ConnectSmtpInput,
  Count,
  ImportResult,
  Sender,
  SenderList,
  TestConnectionInput,
  TestOutcome,
  UpdateSenderInput,
  UpdateWarmupInput,
  WarmupStats,
} from "./sender.schemas.ts";

/** Every call is scoped to the caller's active organization (ADR-001 §5); `userId` is the author. */
type ForOrganization = { readonly userId: string; readonly organizationId: string };

export interface SenderApi extends EraseStep {
  /** The current project's email accounts, shared by the organization; the plan's limit is the organization's. */
  list(input: ForOrganization): Promise<SenderList>;
  get(input: ForOrganization & { readonly senderId: string }): Promise<Sender>;
  connectOAuth(input: ForOrganization & ConnectOAuthInput): Promise<Sender>;
  connectApiKey(input: ForOrganization & ConnectApiKeyInput): Promise<Sender>;
  connectSmtp(input: ForOrganization & ConnectSmtpInput): Promise<Sender>;
  testConnection(input: ForOrganization & TestConnectionInput): Promise<TestOutcome>;
  update(input: ForOrganization & UpdateSenderInput): Promise<Sender>;
  /** Warmup is a plan feature, read from the organization's billing. */
  updateWarmup(input: ForOrganization & UpdateWarmupInput): Promise<Sender>;
  warmupStats(input: ForOrganization & { readonly senderId: string }): Promise<WarmupStats>;
  /** Re-checks connections; accounts being set up become ready when their time comes. */
  refresh(input: ForOrganization): Promise<Count>;
  bulkEdit(input: ForOrganization & BulkEditInput): Promise<BulkEditResult>;
  importCsv(input: ForOrganization & { readonly csv: string }): Promise<ImportResult>;
  remove(input: ForOrganization & { readonly senderIds: readonly string[] }): Promise<Count>;
  moveToProject(
    input: ForOrganization & { readonly senderIds: readonly string[]; readonly projectId: string },
  ): Promise<Count>;
  /** Managed Mailboxes hands over a mailbox it bought; it starts provisioning. */
  adoptPurchased(
    input: ForOrganization & {
      readonly email: string;
      readonly name: string;
      readonly provider: "smtp" | "gmail" | "outlook";
    },
  ): Promise<Sender>;
  /** Operator-only (D053): no transport door. Stamps rows left over from before S1.03. */
  backfillOrganizationId(): Promise<{ readonly assigned: number }>;
}

export const SenderApi = moduleApi<SenderApi>()("sender");
